(function () {
  if (customElements.get('parallax-image')) return;

  class ParallaxImage extends HTMLElement {
    connectedCallback() {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }

      this.target = this.querySelector('img');
      if (!this.target) return;

      this.mode = this.getAttribute('mode') === 'fixed' ? 'fixed' : 'move';
      this.scale = parseFloat(this.getAttribute('scale')) || 1.2;
      const intensity = parseFloat(this.getAttribute('intensity'));
      const intensityPct = isNaN(intensity) ? 100 : Math.max(0, Math.min(100, intensity));
      const maxTranslatePct = ((this.scale - 1) * 100) / (2 * this.scale);
      this.translatePct = maxTranslatePct * (intensityPct / 100);

      this.kenburns = this.getAttribute('kenburns');
      if (this.kenburns !== 'in' && this.kenburns !== 'out') this.kenburns = null;
      const kbAmount = parseFloat(this.getAttribute('kenburns-amount'));
      this.kenburnsDelta = this.kenburns ? Math.max(0.05, Math.min(0.5, (isNaN(kbAmount) ? 15 : kbAmount) / 100)) : 0;

      this.style.display = this.style.display || 'block';
      this.style.position = this.style.position || 'relative';
      this.style.overflow = 'hidden';

      if (this.mode === 'fixed') {
        // overflow:hidden schneidet position:fixed-Kinder nicht ab — clip-path schon
        this.style.clipPath = 'inset(0)';
      }

      this.target.style.willChange = 'transform';

      this.ticking = false;
      this.lastTransform = null;
      this.active = false;

      this._update = this._update.bind(this);
      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);

      if (this.mode === 'fixed') this._layoutFixed();
      this._computeStart();

      // Ohne Ken-Burns braucht der Fest-Modus keine Scroll-Updates:
      // das Bild steht per position:fixed von selbst still.
      const needsScroll = this.mode === 'move' || this.kenburns !== null;

      if (needsScroll) {
        if ('IntersectionObserver' in window) {
          this._io = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (!this.active) {
                  window.addEventListener('scroll', this._onScroll, { passive: true });
                  this.active = true;
                }
                this._computeStart();
                this._update();
              } else if (this.active) {
                window.removeEventListener('scroll', this._onScroll);
                this.active = false;
              }
            });
          }, { rootMargin: '20% 0px 20% 0px' });
          this._io.observe(this);
        } else {
          window.addEventListener('scroll', this._onScroll, { passive: true });
          this.active = true;
        }
      }

      window.addEventListener('resize', this._onResize, { passive: true });
      window.addEventListener('load', this._onResize);
      this._update();
    }

    disconnectedCallback() {
      if (this._io) this._io.disconnect();
      if (this.active) window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('load', this._onResize);
    }

    _onScroll() {
      if (!this.ticking) {
        window.requestAnimationFrame(this._update);
        this.ticking = true;
      }
    }

    _onResize() {
      if (this.mode === 'fixed') this._layoutFixed();
      this._computeStart();
      this._onScroll();
    }

    // "Fest im Hintergrund": Das Bild wird viewport-fixiert und vom Container
    // per clip-path beschnitten — die Sektion gleitet wie ein Fenster darüber.
    // Die horizontale Lage des Containers ändert sich beim Scrollen nicht,
    // daher genügt es, left/width bei Layout-Änderungen neu zu setzen.
    _layoutFixed() {
      const rect = this.getBoundingClientRect();
      const s = this.target.style;
      s.position = 'fixed';
      s.top = '0';
      s.left = rect.left + 'px';
      s.width = rect.width + 'px';
      s.height = window.CSS && CSS.supports('height', '100lvh')
        ? '100lvh'
        : (window.innerHeight || document.documentElement.clientHeight) + 'px';
      s.minWidth = '0';
      s.minHeight = '0';
      s.maxWidth = 'none';
      s.maxHeight = 'none';
      s.objectFit = 'cover';
    }

    // Sections, die schon bei Scrollposition 0 sichtbar sind (z.B. Hero ganz oben),
    // starten sonst mitten in der Animationsstrecke und spielen nur einen Teil
    // der Bewegung aus. startProgress hält den Fortschritt bei Scrollposition 0,
    // damit _update auf die volle Strecke normalisieren kann.
    _computeStart() {
      const rect = this.getBoundingClientRect();
      const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const docTop = rect.top + scrollY;
      const range = vh + rect.height;
      this.startProgress = range > 0 ? (vh - docTop) / range : 0;
    }

    _update() {
      this.ticking = false;
      if (this.mode === 'fixed' && !this.kenburns) return;

      const rect = this.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const traveled = vh - rect.top;
      const range = vh + rect.height;
      let p = range > 0 ? traveled / range : 0;
      if (this.startProgress > 0 && this.startProgress < 1) {
        p = (p - this.startProgress) / (1 - this.startProgress);
      }
      if (p < 0) p = 0;
      else if (p > 1) p = 1;

      // Ken-Burns interpoliert den Zoom entlang des Scroll-Fortschritts.
      // Der kleinste erreichte Zoom ist immer der Basiswert (this.scale),
      // damit die aus this.scale berechnete Translate-Grenze nie Bildränder freigibt.
      let scaleNow = this.mode === 'fixed' ? 1 : this.scale;
      if (this.kenburns === 'in') scaleNow += this.kenburnsDelta * p;
      else if (this.kenburns === 'out') scaleNow += this.kenburnsDelta * (1 - p);

      let transform = 'scale(' + scaleNow + ')';
      if (this.mode === 'move') {
        const pct = (p - 0.5) * 2 * this.translatePct;
        transform += ' translateY(' + pct + '%)';
      }
      if (transform !== this.lastTransform) {
        this.lastTransform = transform;
        this.target.style.transform = transform;
      }
    }
  }

  customElements.define('parallax-image', ParallaxImage);
})();
